export const MAP_PROVIDER_STORAGE_KEY = "gps-web:map-provider";

export const MAP_PROVIDERS = {
  GOOGLE: "google",
  OSM: "osm",
};

export const MAP_PROVIDER_OPTIONS = [
  { value: MAP_PROVIDERS.GOOGLE, label: "Google Maps" },
  { value: MAP_PROVIDERS.OSM, label: "OpenStreetMap" },
];

const OSM_RASTER_TILE_TEMPLATES = [
  "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
];

const OSM_RASTER_TILE_TEMPLATES_DARK = [
  "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
];

const toFinite = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toWrappedLng = (lng) => {
  const parsed = Number(lng);
  if (!Number.isFinite(parsed)) return null;
  if (parsed > 180) return parsed - 360;
  if (parsed < -180) return parsed + 360;
  return parsed;
};

const toGoogleLikeLatLng = (lat, lng) => ({
  lat: () => Number(lat),
  lng: () => Number(lng),
});

const toGoogleLikeBounds = (bounds) => {
  const north = toFinite(bounds?.north);
  const east = toFinite(bounds?.east);
  const south = toFinite(bounds?.south);
  const west = toFinite(bounds?.west);
  if (![north, east, south, west].every(Number.isFinite)) return null;
  return {
    getNorthEast: () => toGoogleLikeLatLng(north, east),
    getSouthWest: () => toGoogleLikeLatLng(south, west),
  };
};

export const normalizeMapCenter = (center, fallback = { lat: 20, lng: 0 }) => {
  const lat =
    typeof center?.lat === "function" ? center.lat() : toFinite(center?.lat, fallback.lat);
  const lngRaw = typeof center?.lng === "function" ? center.lng() : center?.lng;
  const lng = toWrappedLng(lngRaw);
  return {
    lat: Number.isFinite(lat) ? lat : fallback.lat,
    lng: Number.isFinite(lng) ? lng : fallback.lng,
  };
};

export const getPlainMapView = (map, fallback = { center: { lat: 20, lng: 0 }, zoom: 2 }) => {
  if (!map) return fallback;
  const center = normalizeMapCenter(map.getCenter?.(), fallback.center);
  const zoom = toFinite(map.getZoom?.(), fallback.zoom);
  return {
    center,
    zoom: Number.isFinite(zoom) ? zoom : fallback.zoom,
  };
};

export const getBoundsSnapshotFromPoints = (points = []) => {
  const validPoints = (Array.isArray(points) ? points : []).filter(
    (point) => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng))
  );
  if (!validPoints.length) return null;

  const north = Math.max(...validPoints.map((point) => Number(point.lat)));
  const south = Math.min(...validPoints.map((point) => Number(point.lat)));
  const east = Math.max(...validPoints.map((point) => Number(point.lng)));
  const west = Math.min(...validPoints.map((point) => Number(point.lng)));
  return { north, east, south, west };
};

export const fitMapToPoints = (map, points = [], padding = 80) => {
  if (!map) return;
  const bounds = getBoundsSnapshotFromPoints(points);
  if (!bounds) return;

  if (map.__providerType === MAP_PROVIDERS.OSM) {
    map.fitBounds(
      [
        [bounds.west, bounds.south],
        [bounds.east, bounds.north],
      ],
      padding
    );
    return;
  }

  if (window.google?.maps?.LatLngBounds && typeof map.fitBounds === "function") {
    const googleBounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => {
      if (!Number.isFinite(Number(point?.lat)) || !Number.isFinite(Number(point?.lng))) return;
      googleBounds.extend({ lat: Number(point.lat), lng: Number(point.lng) });
    });
    if (!googleBounds.isEmpty?.()) {
      map.fitBounds(googleBounds, padding);
    }
  }
};

export const createMapLibreAdapter = (map) => ({
  __providerType: MAP_PROVIDERS.OSM,
  __rawMap: map,
  panTo: ({ lat, lng }) => {
    if (!map) return;
    map.easeTo({
      center: [Number(lng), Number(lat)],
      duration: 600,
      essential: true,
    });
  },
  setZoom: (zoom) => {
    if (!map) return;
    map.easeTo({
      zoom: Number(zoom),
      duration: 250,
      essential: true,
    });
  },
  getZoom: () => Number(map?.getZoom?.() || 0),
  getCenter: () => {
    const center = map?.getCenter?.();
    return toGoogleLikeLatLng(center?.lat, center?.lng);
  },
  getBounds: () => {
    const bounds = map?.getBounds?.();
    if (!bounds) return null;
    return toGoogleLikeBounds({
      north: bounds.getNorth?.(),
      east: bounds.getEast?.(),
      south: bounds.getSouth?.(),
      west: bounds.getWest?.(),
    });
  },
  fitBounds: (boundsInput, padding = 80) => {
    if (!map) return;
    if (Array.isArray(boundsInput)) {
      const bounds = getBoundsSnapshotFromPoints(boundsInput);
      if (!bounds) return;
      map.fitBounds(
        [
          [bounds.west, bounds.south],
          [bounds.east, bounds.north],
        ],
        {
          padding,
          duration: 600,
          essential: true,
        }
      );
      return;
    }

    const northEast = boundsInput?.getNorthEast?.();
    const southWest = boundsInput?.getSouthWest?.();
    const north = typeof northEast?.lat === "function" ? northEast.lat() : northEast?.lat;
    const east = typeof northEast?.lng === "function" ? northEast.lng() : northEast?.lng;
    const south = typeof southWest?.lat === "function" ? southWest.lat() : southWest?.lat;
    const west = typeof southWest?.lng === "function" ? southWest.lng() : southWest?.lng;
    if (![north, east, south, west].every((value) => Number.isFinite(Number(value)))) return;
    map.fitBounds(
      [
        [Number(west), Number(south)],
        [Number(east), Number(north)],
      ],
      {
        padding,
        duration: 600,
        essential: true,
      }
    );
  },
});

export const resolveOsmStyleUrl = (theme = "light") => {
  const themedStyle =
    theme === "dark"
      ? process.env.REACT_APP_OSM_DARK_STYLE_URL
      : process.env.REACT_APP_OSM_LIGHT_STYLE_URL;
  if (themedStyle) return themedStyle;
  if (process.env.REACT_APP_OSM_STYLE_URL) return process.env.REACT_APP_OSM_STYLE_URL;
  return null;
};

export const createFallbackRasterStyle = (theme = "light") => ({
  version: 8,
  name: theme === "dark" ? "osm-dark-fallback" : "osm-light-fallback",
  sources: {
    "carto-raster": {
      type: "raster",
      tiles: theme === "dark" ? OSM_RASTER_TILE_TEMPLATES_DARK : OSM_RASTER_TILE_TEMPLATES,
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  layers: [
    {
      id: "osm-background",
      type: "background",
      paint: {
        "background-color": theme === "dark" ? "#0f172a" : "#f8fafc",
      },
    },
    {
      id: "carto-raster-layer",
      type: "raster",
      source: "carto-raster",
      minzoom: 0,
      maxzoom: 22,
      paint: {
        "raster-opacity": 1,
        "raster-resampling": "linear",
      },
    },
  ],
});
